import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { SERVICES, BUSINESS } from "../lib/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  ...SERVICES.map((s) => s.title),
  "Furniture Assembly",
  "TV Mounting / Picture Hanging",
  "Caulking & Sealing",
  "Smart Home Install",
  "Other / Not sure — advise me",
];

const inputClass =
  "bg-[#14110F] border-[#332C27] text-[#F9FAFB] placeholder:text-[#6b645e] focus:border-[#F5A623] focus:ring-[#F5A623] rounded-xl h-12";

export default function QuoteForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    address: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Pre-fill from Services click
  useEffect(() => {
    const handler = (e) => {
      const title = e.detail;
      if (title) setForm((f) => ({ ...f, service: title }));
    };
    window.addEventListener("prefill-service", handler);
    return () => window.removeEventListener("prefill-service", handler);
  }, []);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const validate = () => {
    if (!form.name.trim()) return "Please add your name.";
    if (!form.email.trim() || !form.email.includes("@"))
      return "A valid email helps us reply fast.";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7)
      return "We need a good phone number to reach you.";
    if (!form.service) return "Pick the service you need.";
    if (!form.message.trim() || form.message.trim().length < 8)
      return "Tell us a little about the job (min 8 characters).";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/quote-requests`, form);
      toast.success("Quote request sent! We'll reach out shortly.");
      setDone(true);
      setForm({
        name: "",
        email: "",
        phone: "",
        service: "",
        address: "",
        message: "",
      });
    } catch (e2) {
      console.error(e2);
      toast.error("Couldn't send right now. Please call us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="py-24 md:py-32 border-t border-[#332C27] bg-radial-amber"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5 reveal">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#F5A623] font-bold mb-4">
            / 05 — Request a quote
          </div>
          <h2
            data-testid="quote-heading"
            className="font-display text-4xl md:text-5xl lg:text-6xl text-[#F9FAFB] font-semibold uppercase tracking-tight leading-[1.05]"
          >
            Tell us what
            <br />
            <span className="text-[#F5A623]">needs fixing.</span>
          </h2>
          <p className="mt-8 text-[#A19D98] text-base md:text-lg leading-relaxed">
            Free, no-pressure quotes — usually within the same day. Prefer to
            talk? Call{" "}
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              className="text-[#F5A623] hover:underline"
            >
              {BUSINESS.phone}
            </a>{" "}
            and ask for the family.
          </p>

          <ul className="mt-10 space-y-4">
            {[
              "Free estimates, clear up-front pricing",
              "Same-day response, most jobs booked within the week",
              "Licensed, insured, 5-star-rated",
            ].map((b) => (
              <li key={b} className="flex items-start gap-3 text-[#F9FAFB]">
                <span className="mt-1 block w-1.5 h-1.5 rounded-full bg-[#F5A623]" />
                <span className="text-sm md:text-base">{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7 reveal">
          {done ? (
            <div
              data-testid="quote-success"
              className="bg-[#1E1A18] border border-[#F5A623]/40 rounded-2xl p-10 text-center"
            >
              <div className="text-5xl mb-4">★★★★★</div>
              <h3 className="font-display text-3xl text-[#F9FAFB] font-semibold tracking-tight">
                Thanks — message received.
              </h3>
              <p className="text-[#A19D98] mt-4 max-w-md mx-auto">
                One of the family will reach out within a few hours (during
                business hours). Need it sooner? Call us directly.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href={`tel:${BUSINESS.phoneRaw}`}
                  data-testid="success-call-button"
                  className="inline-flex items-center justify-center gap-2 bg-[#F5A623] text-[#14110F] hover:bg-[#D98C1A] px-6 py-3 rounded-full text-sm font-bold uppercase tracking-[0.15em] transition-colors"
                >
                  Call {BUSINESS.phone}
                </a>
                <button
                  data-testid="success-new-request"
                  onClick={() => setDone(false)}
                  className="inline-flex items-center justify-center gap-2 border border-[#332C27] text-[#F9FAFB] hover:border-[#F5A623] px-6 py-3 rounded-full text-sm font-semibold uppercase tracking-[0.15em] transition-colors"
                >
                  Send another request
                </button>
              </div>
            </div>
          ) : (
            <form
              data-testid="quote-form"
              onSubmit={submit}
              className="bg-[#1E1A18] border border-[#332C27] rounded-2xl p-6 md:p-10 space-y-5"
              noValidate
            >
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label
                    htmlFor="q-name"
                    className="text-xs uppercase tracking-[0.18em] text-[#A19D98] font-bold"
                  >
                    Your Name
                  </Label>
                  <Input
                    id="q-name"
                    data-testid="quote-name-input"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Doe"
                    className={`${inputClass} mt-2`}
                  />
                </div>
                <div>
                  <Label
                    htmlFor="q-phone"
                    className="text-xs uppercase tracking-[0.18em] text-[#A19D98] font-bold"
                  >
                    Phone
                  </Label>
                  <Input
                    id="q-phone"
                    data-testid="quote-phone-input"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="(239) 555-0199"
                    className={`${inputClass} mt-2`}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label
                    htmlFor="q-email"
                    className="text-xs uppercase tracking-[0.18em] text-[#A19D98] font-bold"
                  >
                    Email
                  </Label>
                  <Input
                    id="q-email"
                    data-testid="quote-email-input"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="jane@example.com"
                    className={`${inputClass} mt-2`}
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#A19D98] font-bold">
                    Service Needed
                  </Label>
                  <Select
                    value={form.service}
                    onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}
                  >
                    <SelectTrigger
                      data-testid="quote-service-select"
                      className={`${inputClass} mt-2`}
                    >
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E1A18] border-[#332C27] text-[#F9FAFB]">
                      {SERVICE_OPTIONS.map((opt) => (
                        <SelectItem
                          key={opt}
                          value={opt}
                          className="focus:bg-[#F5A623]/10 focus:text-[#F5A623]"
                        >
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label
                  htmlFor="q-address"
                  className="text-xs uppercase tracking-[0.18em] text-[#A19D98] font-bold"
                >
                  Service Address{" "}
                  <span className="text-[#6b645e] normal-case tracking-normal">
                    (optional)
                  </span>
                </Label>
                <Input
                  id="q-address"
                  data-testid="quote-address-input"
                  value={form.address}
                  onChange={update("address")}
                  placeholder="Street, City, FL"
                  className={`${inputClass} mt-2`}
                />
              </div>

              <div>
                <Label
                  htmlFor="q-message"
                  className="text-xs uppercase tracking-[0.18em] text-[#A19D98] font-bold"
                >
                  Describe the Job
                </Label>
                <Textarea
                  id="q-message"
                  data-testid="quote-message-input"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Ceiling fan install in master bedroom, 9ft ceilings, existing wiring in place…"
                  rows={5}
                  className={`${inputClass} mt-2 h-auto py-3`}
                />
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="quote-submit-button"
                  className="group inline-flex items-center justify-center gap-2 bg-[#F5A623] text-[#14110F] hover:bg-[#D98C1A] disabled:opacity-60 disabled:cursor-not-allowed px-7 py-4 rounded-full text-sm font-bold uppercase tracking-[0.15em] transition-colors"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Send Request
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </button>
                <p className="text-xs text-[#A19D98]">
                  By submitting, you agree we may contact you about your
                  request. We never share your info.
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
